import { useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { getArticleBySlug, getRelatedArticles } from "@/mocks/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import StructuredData from "@/components/feature/StructuredData";
import ArticleCard from "@/components/feature/ArticleCard";
import type { BreadcrumbItem } from "@/components/feature/Breadcrumb";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const article = getArticleBySlug(slug || "");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: "首页", href: "/" },
    { label: article.categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      "@type": "Organization",
      name: "P站视频指南",
    },
  };

  return (
    <>
      <StructuredData data={articleSchema} />
      <Navbar />
      <main className="pt-24 md:pt-28 pb-14 md:pb-20">
        <article className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full hover:bg-secondary-200 transition-colors whitespace-nowrap"
                >
                  {article.categoryName}
                </Link>
                <span className="text-sm text-foreground-400">{article.readTime}</span>
                <span className="text-sm text-foreground-400">
                  <i className="ri-eye-line mr-1"></i>
                  {article.views.toLocaleString()} 次阅读
                </span>
                <span className="text-sm text-foreground-400">{article.publishDate}</span>
              </div>

              <h1
                className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {article.title}
              </h1>

              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>

              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 bg-background-100 text-foreground-500 rounded-full border border-background-200/70"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
                title={`${article.title} - P站视频指南`}
              />
            </div>

            <div className="prose-custom max-w-none">
              {article.content.map((section, index) => {
                switch (section.type) {
                  case "heading":
                    const HeadingTag = `h${section.level || 2}` as keyof JSX.IntrinsicElements;
                    return (
                      <HeadingTag
                        key={index}
                        className={`font-semibold text-foreground-950 mt-8 mb-4 ${
                          section.level === 2
                            ? "text-xl md:text-2xl"
                            : section.level === 3
                            ? "text-lg md:text-xl"
                            : "text-base md:text-lg"
                        }`}
                        style={{ fontFamily: "var(--font-heading)" }}
                      >
                        {section.content}
                      </HeadingTag>
                    );
                  case "paragraph":
                    return (
                      <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                        {section.content}
                      </p>
                    );
                  case "list":
                    return (
                      <ul key={index} className="list-disc pl-5 mb-4 space-y-1">
                        {section.items?.map((item, i) => (
                          <li key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  case "tip":
                    return (
                      <div
                        key={index}
                        className="bg-accent-100 border-l-4 border-accent-500 rounded-r-lg p-4 md:p-5 mb-4"
                      >
                        <div className="flex items-start gap-3">
                          <i className="ri-lightbulb-line text-accent-600 text-lg flex-shrink-0 mt-0.5"></i>
                          <p className="text-sm md:text-base text-accent-800 leading-relaxed">
                            {section.content}
                          </p>
                        </div>
                      </div>
                    );
                  default:
                    return null;
                }
              })}
            </div>

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <Link
                  to={`/category/${article.category}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors"
                >
                  ← 返回{article.categoryName}分类
                </Link>
                <span className="text-xs text-foreground-400">
                  作者：{article.author} · 发布于 {article.publishDate}
                </span>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 mt-14 md:mt-20">
            <div className="max-w-7xl mx-auto">
              <h2
                className="text-xl md:text-3xl font-bold text-foreground-950 mb-8"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}